import axios from 'axios'
import toast from 'react-hot-toast'

// Create axios instance
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add token from localStorage if available
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage)
        const token = parsed.state?.token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (error) {
        // Invalid storage data, clear it
        localStorage.removeItem('auth-storage')
      }
    }
    
    // Add cache control headers instead of query parameter
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
      config.headers['Pragma'] = 'no-cache'
      config.headers['Expires'] = '0'
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          if (!window.location.pathname.includes('/auth/login')) {
            const message = data?.message || ''
            const isJWTExpired = message.toLowerCase().includes('jwt') || 
                                message.toLowerCase().includes('token') ||
                                message.toLowerCase().includes('expired')
            
            // Clear all auth-related data
            localStorage.removeItem('auth-storage')
            sessionStorage.clear()
            
            // Clear any cached data
            if ('caches' in window) {
              caches.keys().then(names => {
                names.forEach(name => {
                  caches.delete(name)
                })
              })
            }
            
            // Show appropriate message
            if (isJWTExpired) {
              toast.error('세션이 만료되었습니다. 다시 로그인해주세요.')
            } else {
              toast.error('Session expired. Please login again.')
            }
            
            // Redirect to login after a short delay
            setTimeout(() => {
              window.location.href = '/auth/login'
            }, 1000)
          }
          break
          
        case 403:
          toast.error('Access denied')
          break
          
        case 404:
          toast.error('Resource not found')
          break
          
        case 422:
          // Validation errors
          if (data.error?.details) {
            data.error.details.forEach(detail => {
              toast.error(detail.message)
            })
          } else {
            toast.error(data.error?.message || 'Validation error')
          }
          break
          
        case 429:
          toast.error('Too many requests. Please try again later.')
          break
          
        case 500:
          toast.error('Server error. Please try again later.')
          break
          
        default:
          toast.error(data.error?.message || 'An error occurred')
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.')
    } else {
      // Other error
      toast.error('An unexpected error occurred')
    }
    
    return Promise.reject(error)
  }
)

// API service classes
export class AuthAPI {
  static async login(credentials) {
    const response = await apiClient.post('/auth/login', credentials)
    return response.data
  }
  
  static async register(userData) {
    const response = await apiClient.post('/auth/admin/register', userData)
    return response.data
  }
  
  static async getProfile() {
    const response = await apiClient.get('/auth/me')
    return response.data
  }
  
  static async logout() {
    // In a real app, you might want to call a logout endpoint
    return Promise.resolve()
  }
}

export class UserAPI {
  static async getUsers(params = {}) {
    // Filter out empty values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/users', { params: cleanParams })
    return response.data
  }
  
  static async getUserById(id) {
    const response = await apiClient.get(`/users/${id}`)
    return response.data
  }
  
  static async searchUsers(params) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/users/search', { params: cleanParams })
    return response.data
  }
  
  static async updateUserBalance(balanceData) {
    const response = await apiClient.put('/users/balance', balanceData)
    return response.data
  }
  
  static async addAdminMemo(memoData) {
    const response = await apiClient.post('/users/memo', memoData)
    return response.data
  }
  
  static async getUserMemos(userId, params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get(`/users/${userId}/memos`, { params: cleanParams })
    return response.data
  }
  
  static async getUserTransactions(userId, params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get(`/users/${userId}/transactions`, { params: cleanParams })
    return response.data
  }
  
  static async getBalanceSummary() {
    const response = await apiClient.get('/users/balance-summary')
    return response.data
  }
}

export class ExportAPI {
  static async getExportInfo() {
    const response = await apiClient.get('/export')
    return response.data
  }
  
  static async exportUsers(params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/export/users', {
      params: cleanParams,
      responseType: 'blob'
    })
    return response
  }
  
  static async exportTransactions(params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/export/transactions', {
      params: cleanParams,
      responseType: 'blob'
    })
    return response
  }
  
  static async exportReferralCodes(params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/export/referral-codes', {
      params: cleanParams,
      responseType: 'blob'
    })
    return response
  }
  
  static async exportSystemData(params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
    )
    const response = await apiClient.get('/export/system-data', {
      params: cleanParams,
      responseType: 'blob'
    })
    return response
  }
}

export class WebSocketAPI {
  static async getConnectionStats() {
    const response = await apiClient.get('/websocket/stats')
    return response.data
  }
  
  static async sendMessageToUser(data) {
    const response = await apiClient.post('/websocket/send-to-user', data)
    return response.data
  }
  
  static async sendSystemAnnouncement(data) {
    const response = await apiClient.post('/websocket/announcement', data)
    return response.data
  }
  
  static async disconnectUser(userId, reason) {
    const response = await apiClient.post(`/websocket/disconnect/${userId}`, { reason })
    return response.data
  }
  
  static async checkUserConnection(userId) {
    const response = await apiClient.get(`/websocket/user/${userId}/status`)
    return response.data
  }
}

// Default export for backward compatibility
const api = {
  ...apiClient,
  auth: AuthAPI,
  users: UserAPI,
  export: ExportAPI,
  websocket: WebSocketAPI
}

export default api